import React from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Card,
    CardBody,
    Badge
} from 'reactstrap';

interface EndpointHelpModalProps {
    isOpen: boolean;
    toggle: () => void;
}

const EndpointHelpModal: React.FC<EndpointHelpModalProps> = ({ isOpen, toggle }) => {
    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered scrollable>
            <ModalHeader toggle={toggle}>
                <i className="ri-information-line me-2"></i>
                How to Create/Edit an Endpoint
            </ModalHeader>

            <ModalBody>
                {/* Introduction */}
                <div className="mb-4">
                    <h5 className="text-primary">
                        <i className="ri-lightbulb-line me-2"></i>
                        What is an Endpoint?
                    </h5>
                    <p className="text-muted">
                        An endpoint is a custom API that processes documents (PDFs, text, or images) using AI.
                        You define what data to extract and how it should be structured, and our AI handles the rest.
                    </p>
                </div>

                {/* 3-Step Wizard Guide */}
                <div className="mb-4">
                    <h5 className="text-primary mb-3">
                        <i className="ri-list-check me-2"></i>
                        3-Step Setup Wizard
                    </h5>

                    {/* Step 1 */}
                    <Card className="border mb-3">
                        <CardBody>
                            <div className="d-flex align-items-center mb-3">
                                <Badge color="primary" className="me-2" style={{ fontSize: '14px', padding: '8px 12px' }}>
                                    Step 1
                                </Badge>
                                <h6 className="mb-0">Basic Information</h6>
                            </div>

                            <div className="mb-3">
                                <strong>What you'll configure:</strong>
                                <ul className="mt-2">
                                    <li>
                                        <strong>Title:</strong> A descriptive name for your endpoint (e.g., "Invoice Data Extractor")
                                    </li>
                                    <li>
                                        <strong>Description:</strong> Explain what this endpoint does
                                    </li>
                                    <li>
                                        <strong>Source Data Type:</strong> Choose what type of input your endpoint will accept:
                                        <ul>
                                            <li><code>PDF/Text File</code> - For documents and text files</li>
                                            <li><code>Text/HTML</code> - For plain text or HTML content</li>
                                            <li><code>Image</code> - For screenshots or scanned documents</li>
                                        </ul>
                                    </li>
                                    <li>
                                        <strong>Endpoint Slug:</strong> The URL path for your API (lowercase, letters, numbers, hyphens only)
                                        <br />
                                        <small className="text-muted">Example: <code>invoice</code> becomes <code>/v1/flow/invoice</code></small>
                                    </li>
                                    <li>
                                        <strong>Status:</strong> Active or Inactive (inactive endpoints cannot be called)
                                    </li>
                                </ul>
                            </div>

                            {/* Screenshot placeholder for Step 1 */}
                            <div className="border rounded p-3 bg-light text-center" style={{ minHeight: '300px' }}>
                                <img
                                    src="/screenshots/endpoint-step1.png"
                                    alt="Step 1: Basic Information"
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '400px' }}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling!.classList.remove('d-none');
                                    }}
                                />
                                <div className="d-none">
                                    <i className="ri-image-line display-4 text-muted"></i>
                                    <p className="text-muted mt-2">Screenshot: Step 1 - Basic Information</p>
                                    <small className="text-muted">Configure endpoint title, description, source type, slug, and status</small>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Step 2 */}
                    <Card className="border mb-3">
                        <CardBody>
                            <div className="d-flex align-items-center mb-3">
                                <Badge color="success" className="me-2" style={{ fontSize: '14px', padding: '8px 12px' }}>
                                    Step 2
                                </Badge>
                                <h6 className="mb-0">AI Configuration & Testing</h6>
                            </div>

                            <div className="mb-3">
                                <strong>What you'll configure:</strong>
                                <ul className="mt-2">
                                    <li>
                                        <strong>Sample Input:</strong> Upload or paste a sample document for testing
                                        <ul>
                                            <li>For PDF/Text: Upload a file or drag & drop</li>
                                            <li>For Text/HTML: Paste your text content</li>
                                            <li>For Image: Upload or paste (Ctrl+V / Cmd+V) an image</li>
                                        </ul>
                                    </li>
                                    <li>
                                        <strong>Prompt:</strong> Instructions for the AI on what data to extract
                                        <br />
                                        <small className="text-muted">
                                            Example: "Extract invoice number, date, vendor name, total amount, and line items from this invoice."
                                        </small>
                                    </li>
                                    <li>
                                        <strong>Expected JSON Format:</strong> Define the structure of the output data
                                        <br />
                                        <small className="text-muted">
                                            Example:
                                        </small>
                                        <pre className="bg-dark text-light p-2 rounded mt-1" style={{ fontSize: '12px' }}>
{`{
  "invoice_number": "string",
  "date": "YYYY-MM-DD",
  "vendor": "string",
  "total": "number",
  "items": [
    {
      "description": "string",
      "quantity": "number",
      "price": "number"
    }
  ]
}`}
                                        </pre>
                                    </li>
                                    <li>
                                        <strong>Test Execution:</strong> Click "Execute" to test your configuration with the sample input
                                        <br />
                                        <small className="text-success">
                                            <i className="ri-lightbulb-line me-1"></i>
                                            The output will appear on the right side. Adjust your prompt and JSON format until you get the desired result.
                                        </small>
                                    </li>
                                </ul>
                            </div>

                            {/* Screenshot placeholder for Step 2 */}
                            <div className="border rounded p-3 bg-light text-center" style={{ minHeight: '300px' }}>
                                <img
                                    src="/screenshots/endpoint-step2.png"
                                    alt="Step 2: AI Configuration"
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '400px' }}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling!.classList.remove('d-none');
                                    }}
                                />
                                <div className="d-none">
                                    <i className="ri-image-line display-4 text-muted"></i>
                                    <p className="text-muted mt-2">Screenshot: Step 2 - AI Configuration</p>
                                    <small className="text-muted">Configure AI prompt, JSON format, and test with sample data</small>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Step 3 */}
                    <Card className="border mb-3">
                        <CardBody>
                            <div className="d-flex align-items-center mb-3">
                                <Badge color="info" className="me-2" style={{ fontSize: '14px', padding: '8px 12px' }}>
                                    Step 3
                                </Badge>
                                <h6 className="mb-0">Review & Save</h6>
                            </div>

                            <div className="mb-3">
                                <strong>Final review:</strong>
                                <ul className="mt-2">
                                    <li>
                                        <strong>Review Configuration:</strong> Check all your settings before saving
                                        <ul>
                                            <li>Endpoint title and URL</li>
                                            <li>Description and source type</li>
                                            <li>AI prompt and expected output format</li>
                                        </ul>
                                    </li>
                                    <li>
                                        <strong>API Endpoint URL:</strong> Copy the generated endpoint URL for use in your applications
                                    </li>
                                    <li>
                                        <strong>Save:</strong> Click "Save" to create/update the endpoint
                                    </li>
                                </ul>

                                <div className="alert alert-success mt-3">
                                    <i className="ri-checkbox-circle-line me-2"></i>
                                    <strong>Success!</strong> Your endpoint will be immediately available for API calls using your API key.
                                </div>
                            </div>

                            {/* Screenshot placeholder for Step 3 */}
                            <div className="border rounded p-3 bg-light text-center" style={{ minHeight: '300px' }}>
                                <img
                                    src="/screenshots/endpoint-step3.png"
                                    alt="Step 3: Review & Save"
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '400px' }}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling!.classList.remove('d-none');
                                    }}
                                />
                                <div className="d-none">
                                    <i className="ri-image-line display-4 text-muted"></i>
                                    <p className="text-muted mt-2">Screenshot: Step 3 - Review & Save</p>
                                    <small className="text-muted">Review your configuration and save the endpoint</small>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                {/* Tips & Best Practices */}
                <div className="mb-3">
                    <h5 className="text-primary">
                        <i className="ri-star-line me-2"></i>
                        Tips & Best Practices
                    </h5>

                    <Card className="border-warning bg-warning-subtle">
                        <CardBody>
                            <h6 className="text-warning">
                                <i className="ri-lightbulb-flash-line me-2"></i>
                                Pro Tips
                            </h6>
                            <ul className="mb-0">
                                <li>
                                    <strong>Test thoroughly:</strong> Always test with multiple sample documents in Step 2 before saving
                                </li>
                                <li>
                                    <strong>Be specific in prompts:</strong> Clear, detailed instructions yield better results
                                    <br />
                                    <small className="text-muted">
                                        ❌ Bad: "Get data from invoice"<br />
                                        ✅ Good: "Extract invoice number, date (in YYYY-MM-DD format), vendor name, total amount, and itemized list with descriptions, quantities, and prices"
                                    </small>
                                </li>
                                <li>
                                    <strong>Use consistent JSON formats:</strong> Match field names and types to your application's needs
                                </li>
                                <li>
                                    <strong>Descriptive slugs:</strong> Use meaningful endpoint slugs (e.g., <code>invoice-extractor</code> instead of <code>endpoint1</code>)
                                </li>
                                <li>
                                    <strong>Inactive for testing:</strong> Set status to "Inactive" while testing, then activate when ready
                                </li>
                            </ul>
                        </CardBody>
                    </Card>
                </div>

                {/* Common Use Cases */}
                <div className="mb-3">
                    <h5 className="text-primary">
                        <i className="ri-apps-line me-2"></i>
                        Common Use Cases
                    </h5>

                    <div className="row g-3">
                        <div className="col-md-6">
                            <Card className="border h-100">
                                <CardBody>
                                    <h6>
                                        <i className="ri-file-text-line me-2 text-primary"></i>
                                        Invoice Processing
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        Extract invoice numbers, dates, vendors, line items, and totals from PDF invoices
                                    </p>
                                </CardBody>
                            </Card>
                        </div>
                        <div className="col-md-6">
                            <Card className="border h-100">
                                <CardBody>
                                    <h6>
                                        <i className="ri-contacts-book-line me-2 text-success"></i>
                                        Resume Parsing
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        Extract contact info, education, work experience, and skills from resumes
                                    </p>
                                </CardBody>
                            </Card>
                        </div>
                        <div className="col-md-6">
                            <Card className="border h-100">
                                <CardBody>
                                    <h6>
                                        <i className="ri-shopping-cart-line me-2 text-warning"></i>
                                        Receipt Digitization
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        Convert receipt images to structured data with merchant, date, items, and total
                                    </p>
                                </CardBody>
                            </Card>
                        </div>
                        <div className="col-md-6">
                            <Card className="border h-100">
                                <CardBody>
                                    <h6>
                                        <i className="ri-hospital-line me-2 text-info"></i>
                                        Medical Forms
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        Extract patient info, diagnosis codes, medications from medical documents
                                    </p>
                                </CardBody>
                            </Card>
                        </div>
                    </div>
                </div>

                {/* Need Help Section */}
                <div className="alert alert-info mb-0">
                    <h6 className="alert-heading">
                        <i className="ri-question-line me-2"></i>
                        Need Help?
                    </h6>
                    <p className="mb-0">
                        If you encounter issues or have questions about endpoint configuration, please contact our support team or refer to the API documentation.
                    </p>
                </div>
            </ModalBody>

            <ModalFooter>
                <Button color="primary" onClick={toggle}>
                    Got it!
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EndpointHelpModal;
